/**
 * Smoke grenades (04 §6). A cloud swells for a second, hangs, then thins away; while it
 * stands nobody sees through it, in either direction - eyes, gun sights, a mini-bunker's
 * detector and a searchlight alike. Bullets pass: smoke hides, it does not stop anything.
 *
 * `clearSight` is `lineOfSight` plus the clouds; everything that looks at the ranger asks it.
 */

import type { Ctx } from './ctx';
import { lineOfSight, segmentHit } from './statics';
import { GEAR } from './tuning';
import type { SmokeCloud, World } from './types';

const LIFE_TICKS = GEAR.smokeGrowTicks + GEAR.smokeHoldTicks + GEAR.smokeFadeTicks;

/** Radius of a cloud with `ticksLeft` to live: up over the first second, down over the last three. */
export function smokeRadiusAt(ticksLeft: number): number {
  const age = LIFE_TICKS - ticksLeft;
  if (age < GEAR.smokeGrowTicks) return (GEAR.smokeRadius * Math.max(0, age)) / GEAR.smokeGrowTicks;
  if (ticksLeft < GEAR.smokeFadeTicks) return (GEAR.smokeRadius * Math.max(0, ticksLeft)) / GEAR.smokeFadeTicks;
  return GEAR.smokeRadius;
}

/** A smoke grenade has landed and burst. */
export function releaseSmoke(ctx: Ctx, x: number, z: number): SmokeCloud {
  const w = ctx.world;
  const cloud: SmokeCloud = { id: w.nextId++, x, z, r: 0, ticksLeft: LIFE_TICKS };
  w.smokes.push(cloud);
  ctx.events.push({ type: 'smoke', x, z });
  return cloud;
}

/** Once a ground tick: every cloud ages, and a spent one is gone. */
export function stepSmokes(ctx: Ctx): void {
  const smokes = ctx.world.smokes;
  for (let i = smokes.length - 1; i >= 0; i--) {
    const c = smokes[i];
    if (!c) continue;
    c.ticksLeft--;
    if (c.ticksLeft <= 0) smokes.splice(i, 1);
    else c.r = smokeRadiusAt(c.ticksLeft);
  }
}

/** Does the segment a->b pass through (or start or end in) a cloud thick enough to hide a man? */
export function smokeBlocks(world: World, ax: number, az: number, bx: number, bz: number): boolean {
  for (const c of world.smokes) {
    if (c.r < GEAR.smokeMinRadius) continue;
    const body = {
      propId: -1, shape: 'circle' as const, x: c.x, z: c.z, r: c.r, hx: 0, hz: 0, h: 99,
      blocksMove: false, blocksSight: true, blocksBullets: false,
    };
    if (segmentHit(body, ax, az, bx, bz)) return true;
  }
  return false;
}

/** `lineOfSight`, and no smoke in the way. */
export function clearSight(
  ctx: Pick<Ctx, 'world' | 'statics'>,
  ax: number,
  az: number,
  ah: number,
  bx: number,
  bz: number,
  bh: number,
  ignoreProp = -1,
): boolean {
  if (ctx.world.smokes.length > 0 && smokeBlocks(ctx.world, ax, az, bx, bz)) return false;
  return lineOfSight(ctx.statics, ax, az, ah, bx, bz, bh, ignoreProp);
}
